import { useEffect, useState, type FormEvent } from "react";
import { useProfiles } from "../profiles/ProfileContext";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { EQUIPMENT_TYPE_OPTIONS } from "../lib/loads";
import {
  listVehicles, createVehicle, setVehicleStatus, assignVehicle,
  VEHICLE_TYPES, VEHICLE_STATUSES, type Vehicle, type VehicleStatus,
} from "../lib/fleet";

const BLANK = { unit_number: "", type: "truck", make: "", model: "", year: "", equipment_type: "", plate: "", assigned_to: "" };

export function Fleet() {
  const { active } = useProfiles();
  const isCompany = active?.type === "company";
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [busy, setBusy] = useState<string | null>(null);
  const [editAssign, setEditAssign] = useState<string | null>(null);
  const [assignDraft, setAssignDraft] = useState("");

  async function refresh() {
    setVehicles((await listVehicles()).vehicles);
  }
  useEffect(() => {
    if (!isCompany) { setLoading(false); return; }
    refresh().finally(() => setLoading(false));
  }, [isCompany]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!form.unit_number.trim()) return;
    setBusy("new");
    try {
      await createVehicle({ ...form, year: form.year ? Number(form.year) : undefined });
      setForm({ ...BLANK });
      setShowForm(false);
      await refresh();
    } finally { setBusy(null); }
  }

  async function changeStatus(v: Vehicle, status: VehicleStatus) {
    setBusy(v.id);
    try { await setVehicleStatus(v.id, status); await refresh(); } finally { setBusy(null); }
  }

  async function saveAssign(v: Vehicle) {
    setBusy(v.id);
    try {
      await assignVehicle(v.id, assignDraft.trim() || null);
      setEditAssign(null);
      await refresh();
    } finally { setBusy(null); }
  }

  const specLine = (v: Vehicle) =>
    [labelize(v.type), [v.year, v.make, v.model].filter(Boolean).join(" "), v.equipment_type ? labelize(v.equipment_type) : "", v.plate]
      .filter(Boolean).join(" · ");

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Fleet"
        subtitle="Track your trucks and trailers, their status, and who's driving them."
        actions={isCompany ? <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "Add vehicle"}</Button> : undefined}
      />

      {!isCompany ? (
        <p className="text-sm text-muted">Switch to a company profile to manage a fleet.</p>
      ) : (
        <>
          {showForm && (
            <Panel title="New vehicle">
              <form className="grid gap-3 sm:grid-cols-2" onSubmit={create}>
                <input className={inputClass} placeholder="Unit number" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} required aria-label="Unit number" />
                <select className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} aria-label="Type">
                  {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
                </select>
                <input className={inputClass} placeholder="Make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
                <input className={inputClass} placeholder="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                <input className={inputClass} placeholder="Year" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
                <select className={inputClass} value={form.equipment_type} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })} aria-label="Equipment type">
                  <option value="">Equipment type…</option>
                  {EQUIPMENT_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input className={inputClass} placeholder="Plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
                <input className={inputClass} placeholder="Assigned driver" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} />
                <Button type="submit" disabled={busy === "new" || !form.unit_number.trim()} className="sm:col-span-2">Add vehicle</Button>
              </form>
            </Panel>
          )}

          {loading ? (
            <p className="font-mono text-sm text-muted">Loading…</p>
          ) : vehicles.length === 0 ? (
            <p className="font-mono text-sm text-muted">No vehicles yet. Add your first truck or trailer.</p>
          ) : (
            <ul className="grid gap-3">
              {vehicles.map((v) => (
                <li key={v.id} className="grid gap-2 rounded-card border border-line bg-surface p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-display text-lg font-black tracking-tight">{v.unit_number}</span>
                    <StatusLabel status={v.status} />
                  </div>
                  <p className="font-mono text-[11px] text-muted">{specLine(v)}</p>

                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted">Driver:</span>
                    {editAssign === v.id ? (
                      <>
                        <input className={`${inputClass} h-8 w-40 text-sm`} value={assignDraft} onChange={(e) => setAssignDraft(e.target.value)} placeholder="Driver name" aria-label="Assign driver" />
                        <Button variant="ghost" disabled={busy === v.id} onClick={() => saveAssign(v)}>Save</Button>
                      </>
                    ) : (
                      <button type="button" className="font-bold hover:text-accent-strong" onClick={() => { setEditAssign(v.id); setAssignDraft(v.assigned_to ?? ""); }}>
                        {v.assigned_to || "Unassigned"}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted">Status:</span>
                    {VEHICLE_STATUSES.filter((s) => s !== v.status).map((s) => (
                      <button key={s} type="button" disabled={busy === v.id}
                        className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-muted hover:border-accent hover:text-accent-strong"
                        onClick={() => changeStatus(v, s)}>→ {labelize(s)}</button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
